import { StringFormat } from "../Util/stringutil";
import { constants } from "../constants";
import {
  CorpDetailAPIEntity,
  CorporationStatsEntity,
  InsertCorpAPIEntity,
  InsertCorpReturnAPIEntity,
  InvoiceData,
  PatchPolicyRequest,
  PatchCorpRequest,
  PolicyResponse,
  PutPolicyRequest,
  ResponseCorporationIntegration,
} from "./entity/CorporationOperationEntity";
import { JsonPatchDocument } from "./entity/JasonPatchDocumentEntity";
import { Status } from "./entity/Status";
import { myFetch } from "./myFetch";
import { normalizeUSStateToAlpha2 } from "../Util/stateCode";

// Interface for the Corporation API calls...
export default interface CorporationDataSource {
  getCorpByIdAsync(corpId: number): Promise<Status<CorpDetailAPIEntity>>;
  getCorpStatsByIdAsync(
    corpId: number,
  ): Promise<Status<CorporationStatsEntity>>;
  getCorpAsync(): Promise<Status<CorpDetailAPIEntity[]>>;
  postCorp(
    corpInsert: InsertCorpAPIEntity,
  ): Promise<Status<InsertCorpReturnAPIEntity>>;
  patchCorp(
    corpId: number,
    patchCorporationRequest: JsonPatchDocument<PatchCorpRequest>[],
  ): Promise<Status<null>>;
  getInvoiceAsync(corpId: number): Promise<Status<InvoiceData>>;
  getSubscriptionAsync(corpId: number): Promise<Status<InvoiceData>>;
  getCorpIntegrationsAync(
    corpId: number,
  ): Promise<Status<ResponseCorporationIntegration>>;
  getCorpPoliciesAsync(corpId: number): Promise<Status<PolicyResponse[]>>;
  getCorpPolicySectionAsync(
    corpId: number,
    policyKey: string,
  ): Promise<Status<PolicyResponse>>;
  patchCorpPolicySectionAsync(
    corpId: number,
    policyKey: string,
    request: PatchPolicyRequest,
  ): Promise<Status<PolicyResponse>>;
  putCorpPolicySectionAsync(
    corpId: number,
    policyKey: string,
    request: PutPolicyRequest,
  ): Promise<Status<PolicyResponse>>;
}

export class CorporationOperation implements CorporationDataSource {
  async getCorpPoliciesAsync(
    corpId: number,
  ): Promise<Status<PolicyResponse[]>> {
    const response = await myFetch<Status<PolicyResponse[]>>(
      `${constants.BASE_URL}${StringFormat(
        constants.GET_CORP_POLICIES,
        corpId,
      )}`,
      {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer BLHA`,
        }),
      },
    );

    const data = await response.json();
    return data;
  }

  async getCorpPolicySectionAsync(
    corpId: number,
    policyKey: string,
  ): Promise<Status<PolicyResponse>> {
    const response = await myFetch<Status<PolicyResponse>>(
      `${constants.BASE_URL}${StringFormat(
        constants.GET_CORP_POLICY_SECTION,
        corpId,
        policyKey,
      )}`,
      {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer BLHA`,
        }),
      },
    );

    const data = await response.json();
    return data;
  }

  async patchCorpPolicySectionAsync(
    corpId: number,
    policyKey: string,
    request: PatchPolicyRequest,
  ): Promise<Status<PolicyResponse>> {
    const response = await myFetch<Status<PolicyResponse>>(
      `${constants.BASE_URL}${StringFormat(
        constants.PATCH_CORP_POLICY_SECTION,
        corpId,
        policyKey,
      )}`,
      {
        method: "PATCH",
        headers: new Headers({
          Authorization: ``,
          Accept: "application/json, text/plain",
          "Content-Type": "application/json;charset=UTF-8",
        }),
        body: JSON.stringify(request),
      },
    );

    const data = await response.json();
    return data;
  }

  async putCorpPolicySectionAsync(
    corpId: number,
    policyKey: string,
    request: PutPolicyRequest,
  ): Promise<Status<PolicyResponse>> {
    const response = await myFetch<Status<PolicyResponse>>(
      `${constants.BASE_URL}${StringFormat(
        constants.PUT_CORP_POLICY_SECTION,
        corpId,
        policyKey,
      )}`,
      {
        method: "PUT",
        headers: new Headers({
          Authorization: ``,
          Accept: "application/json, text/plain",
          "Content-Type": "application/json;charset=UTF-8",
        }),
        body: JSON.stringify(request),
      },
    );

    const data = await response.json();
    return data;
  }

  async getCorpIntegrationsAync(
    corpId: number,
  ): Promise<Status<ResponseCorporationIntegration>> {
    const response = await myFetch<Status<ResponseCorporationIntegration>>(
      `${constants.BASE_URL}${StringFormat(
        constants.GET_CORP_INTEGRATIONS,
        corpId,
      )}`,
      {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer BLHA`,
        }),
      },
    );
    // Check if the response is 204 (No Content)
    if (response.status === 204) {
      return {
        statusCode: 204,
        statusMessage: "No Content",
        statusDetails: [],
        data: null,
      };
    }

    const data = await response.json();
    return data;
  }
  async getSubscriptionAsync(corpId: number): Promise<Status<InvoiceData>> {
    const response = await myFetch<Status<InvoiceData>>(
      `${constants.BASE_URL}${StringFormat(
        constants.GET_CORPSUBSCRIPTION,
        corpId,
      )}`,
      {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer BLHA`,
        }),
      },
    );
    const data = await response.json();
    return data;
  }
  async getInvoiceAsync(corpId: number): Promise<Status<InvoiceData>> {
    const response = await myFetch<Status<InvoiceData>>(
      `${constants.BASE_URL}${StringFormat(constants.GET_CORPINVOICE, corpId)}`,
      {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer BLHA`,
        }),
      },
    );
    const data = await response.json();
    return data;
  }

  async postCorp(
    corpInsert: InsertCorpAPIEntity,
  ): Promise<Status<InsertCorpReturnAPIEntity>> {
    const normalizedCorpInsert: InsertCorpAPIEntity = {
      ...corpInsert,
      address: {
        ...corpInsert.address,
        state: normalizeUSStateToAlpha2(corpInsert.address?.state),
      },
      branch: (corpInsert.branch ?? []).map((branch) => ({
        ...branch,
        address: {
          ...branch.address,
          state: normalizeUSStateToAlpha2(branch.address?.state),
        },
      })),
    };

    const response = await myFetch<Status<InsertCorpReturnAPIEntity>>(
      `${constants.BASE_URL}${StringFormat(constants.POST_CORP)}`,
      {
        method: "POST",
        headers: new Headers({
          Authorization: ``,
          Accept: "application/json, text/plain",
          "Content-Type": "application/json;charset=UTF-8",
        }),
        body: JSON.stringify(normalizedCorpInsert),
      },
    );

    const data = await response.json();
    return data;
  }

  async patchCorp(
    corpId: number,
    patchCorporationRequest: JsonPatchDocument<PatchCorpRequest>[],
  ): Promise<Status<null>> {
    const response = await myFetch<Status<null>>(
      `${constants.BASE_URL}${StringFormat(constants.PATCH_CORP, corpId)}`,
      {
        method: "PATCH",
        headers: new Headers({
          Authorization: ``,
          Accept: "application/json, text/plain",
          "Content-Type": "application/json;charset=UTF-8",
        }),
        body: JSON.stringify(patchCorporationRequest),
      },
    );

    const data = await response.json();
    return data;
  }
  /*accessToken: string;

  constructor(accessToken: string) {
    //TODO: look up an alternative solution for initially null context props
    this.accessToken = accessToken;
  }*/
  // Get Corpration detail by Id..
  async getCorpByIdAsync(corpId: number): Promise<Status<CorpDetailAPIEntity>> {
    const response = await myFetch<Status<CorpDetailAPIEntity>>(
      `${constants.BASE_URL}${StringFormat(
        constants.GET_CORP_BY_ID_ENDPOINT,
        corpId,
      )}`,
      {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer BLHA`,
        }),
      },
    );

    const data = await response.json();
    return data;
  }

  async getCorpStatsByIdAsync(
    corpId: number,
  ): Promise<Status<CorporationStatsEntity>> {
    const response = await myFetch<Status<CorporationStatsEntity>>(
      `${constants.BASE_URL}${StringFormat(
        constants.GET_CORP_STATS_BY_ID_ENDPOINT,
        corpId,
      )}`,
      {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer BLHA`,
        }),
      },
    );

    const data = await response.json();
    return data;
  }

  // This will get all Corporation Associated to an logged in user.
  async getCorpAsync(): Promise<Status<CorpDetailAPIEntity[]>> {
    const response = await myFetch<Status<CorpDetailAPIEntity[]>>(
      `${constants.BASE_URL}${constants.GET_CORP_ENDPOINT}`,
      {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer BLHA`,
        }),
      },
    );

    const data = await response.json();
    return data;
  }
}
